import { useCallback, useEffect, useState, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type UseLocalStorageOptions<T> = {
    serializer?: (value: T) => string;
    deserializer?: (value: string) => T;
};

export default function useLocalStorage<T>(
    key: string,
    initialValue: T | (() => T),
    options: UseLocalStorageOptions<T> = {}
): [T, Dispatch<SetStateAction<T>>, () => void] {
    const initialValueRef = useRef(initialValue);
    const keyRef = useRef(key);

    const serializer = useCallback<(value: T) => string>((value) => options.serializer?.(value) ?? JSON.stringify(value), [options.serializer]);

    const deserializer = useCallback<(value: string) => T>((value) => {
        if (options.deserializer) return options.deserializer(value);
        if (value === 'undefined') return undefined as unknown as T;
        try {
            return JSON.parse(value) as T;
        } catch (error) {
            console.error('Parsing error:', error);
            return initialValueRef.current instanceof Function
                ? initialValueRef.current()
                : initialValueRef.current;
        }
    }, [options.deserializer]);

    const readValue = useCallback((): T => {
        try {
            const raw = localStorage.getItem(keyRef.current);
            return raw ? deserializer(raw) : (
                initialValueRef.current instanceof Function
                    ? initialValueRef.current()
                    : initialValueRef.current
            );
        } catch (error) {
            console.warn(`Read error for "${keyRef.current}":`, error);
            return initialValueRef.current instanceof Function
                ? initialValueRef.current()
                : initialValueRef.current;
        }
    }, [deserializer]);

    const [storedValue, setStoredValue] = useState(readValue);

    const setValue: Dispatch<SetStateAction<T>> = useCallback((value) => {
        try {
            setStoredValue((prev) => {
                const newValue = value instanceof Function ? value(prev) : value;
                const serializedNew = serializer(newValue);
                const serializedPrev = serializer(prev);

                if (serializedNew === serializedPrev) return prev;

                localStorage.setItem(keyRef.current, serializedNew);

                // Dispatch custom event for same-tab synchronization
                window.dispatchEvent(new CustomEvent('localStorageChange', {
                    detail: { key: keyRef.current, newValue },
                }));

                return newValue;
            });
        } catch (error) {
            console.warn(`Update error for "${keyRef.current}":`, error);
        }
    },[serializer]);

    const removeValue = useCallback(() => {
        try {
            localStorage.removeItem(keyRef.current);
            const defaultValue = initialValueRef.current instanceof Function
                ? initialValueRef.current()
                : initialValueRef.current;
            setStoredValue(defaultValue);

            // Notify other components in the same tab
            window.dispatchEvent(new CustomEvent('localStorageChange', {
                detail: { key: keyRef.current, newValue: defaultValue },
            }));
        } catch (error) {
            console.warn(`Removal error for "${keyRef.current}":`, error);
        }
    }, []);

    const handleStorageChange = useCallback((event: StorageEvent | CustomEvent<{ key: string; newValue: T }>) => {
        let eventKey: string;
        let newValue: T;

        if (event instanceof StorageEvent) {
            if (event.key !== keyRef.current) return;
            newValue = readValue();
        } else {
            eventKey = event.detail.key;
            newValue = event.detail.newValue;
            if (eventKey !== keyRef.current) return;
        }

        setStoredValue((prev) => {
            const prevSerialized = serializer(prev);
            const newSerialized = serializer(newValue);
            return prevSerialized === newSerialized ? prev : newValue;
        });
    },[serializer, readValue]);

    useEffect(() => {
        const listener = (event: Event) => {
            if (event instanceof StorageEvent) {
                handleStorageChange(event);
            } else if (event.type === 'localStorageChange') {
                handleStorageChange(event as CustomEvent<{ key: string; newValue: T }>);
            }
        };

        window.addEventListener('storage', listener);
        window.addEventListener('localStorageChange', listener as EventListener);

        return () => {
            window.removeEventListener('storage', listener);
            window.removeEventListener('localStorageChange', listener as EventListener);
        };
    }, [handleStorageChange]);

    useEffect(() => {
        if (key !== keyRef.current) {
            keyRef.current = key;
            initialValueRef.current = initialValue;
            setStoredValue(readValue());
        }
    }, [key, initialValue, readValue]);

    return [storedValue, setValue, removeValue];
}